import type { AnswerToken } from '@/entities/chat';
import { extractYoutubeVideoId } from '@/entities/block/lib/blockUrlUtils';

export type YoutubeEmbedRenderStatus = 'loading' | 'embeddable' | 'blocked';

export interface YoutubeEmbedVideo {
  videoId: string;
  title: string;
  url: string;
  thumbnailUrl: string;
}

const iframePattern = /<iframe\b[^>]*\bsrc=["']([^"']+)["'][^>]*>/gi;
const titlePattern = /\btitle=["']([^"']+)["']/i;

const decodeHtmlEntities = (value: string) =>
  value
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');

export const getYoutubeWatchUrl = (videoId: string) =>
  `https://www.youtube.com/watch?v=${videoId}`;

export const getYoutubeThumbnailUrl = (videoId: string) =>
  `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`;

export const extractYoutubeEmbedVideosFromText = (
  text: string
): YoutubeEmbedVideo[] => {
  const videos: YoutubeEmbedVideo[] = [];

  for (const match of text.matchAll(iframePattern)) {
    const iframeHtml = match[0];
    const src = decodeHtmlEntities(match[1] ?? '');
    const videoId = extractYoutubeVideoId(src);

    if (!videoId) {
      continue;
    }

    const title = decodeHtmlEntities(
      iframeHtml.match(titlePattern)?.[1]?.trim() || 'YouTube 영상'
    );

    videos.push({
      videoId,
      title,
      url: getYoutubeWatchUrl(videoId),
      thumbnailUrl: getYoutubeThumbnailUrl(videoId),
    });
  }

  return videos;
};

export const extractYoutubeEmbedVideosFromAnswers = (
  answer: AnswerToken[]
): YoutubeEmbedVideo[] => {
  const seen = new Set<string>();
  const videos: YoutubeEmbedVideo[] = [];

  answer.forEach((item) => {
    extractYoutubeEmbedVideosFromText(item.text).forEach((video) => {
      if (seen.has(video.videoId)) {
        return;
      }

      seen.add(video.videoId);
      videos.push(video);
    });
  });

  return videos;
};
